// ============================================================================
// BEOFLOW LIQUID GLASS EFFECTS
// Synced with iOS Design System (2025-11-30)
// ============================================================================
//
// This file defines reusable glass effect patterns used throughout the app.
// All patterns use the "Liquid Glass" aesthetic from iOS 26.
//
// GLASS FORMULA:
// - Background: color at 5-15% opacity (08, 0D, 1A, 26)
// - Border: 1px solid at 15% opacity (26) or colored border for tints
// - Border Radius: 8px (medium) or 12px (large)
// - No backdrop blur for performance (CSS-only glass)
//
// ============================================================================

// Inline color constants to avoid circular dependency with muiTheme.ts
import { colors } from './BeoflowColors.generated'
import type { Theme } from '@mui/material'

const BEO_WHITE = '#F8F6F4'
const MEAN_BLUE = '#4A9BB8'
const GRAPH_GREY_DARK = '#434647'

// ============================================================================
// BASE GLASS EFFECTS
// ============================================================================

/**
 * Base glass card - neutral white glass with thin border
 * Used for: Cards, containers, panels
 * Occurrences: 22+ in StyleGuidePage
 */
export const glassCard = {
  backgroundColor: `${BEO_WHITE}08`,  // 5% white
  border: `1px solid ${BEO_WHITE}26`, // 15% white
  borderRadius: '12px',
}

/**
 * Small glass card - same as glassCard but smaller radius
 * Used for: Smaller containers, inline elements
 */
export const glassCardSmall = {
  backgroundColor: `${BEO_WHITE}08`,
  border: `1px solid ${BEO_WHITE}26`,
  borderRadius: '8px',
}

/**
 * Glass pill - for chips and pills
 * Used for: Chips, badges, tags
 */
export const glassPill = {
  backgroundColor: `${BEO_WHITE}08`,
  border: `1px solid ${BEO_WHITE}26`,
  borderRadius: '16px',
}

/**
 * Info box glass - slightly darker for info/spec boxes
 * Used for: Specification boxes, info panels
 */
export const glassInfoBox = {
  backgroundColor: `${BEO_WHITE}08`,
  border: `1px solid ${BEO_WHITE}26`,
  borderRadius: '8px',
}

// ============================================================================
// COLOR-TINTED GLASS EFFECTS
// ============================================================================

/**
 * Creates a color-tinted glass effect
 * @param color - The base color (e.g., beoflowColors.peakOrange)
 * @param opacity - Opacity suffix ('26' = 15%, '1A' = 10%, '0D' = 5%)
 */
export const createColorGlass = (color: string, opacity: string = '26') => ({
  backgroundColor: `${color}${opacity}`,
  border: `1px solid ${color}`,
  borderRadius: '8px',
})

/**
 * Creates a color-tinted pill
 */
export const createColorPill = (color: string, opacity: string = '26') => ({
  backgroundColor: `${color}${opacity}`,
  border: `1px solid ${color}`,
  borderRadius: '16px',
})

// Note: Pre-defined color variants removed to avoid circular dependency
// Use createColorGlass() and createColorPill() with specific colors instead

/**
 * Creates selected chip style with color tint
 */
export const createChipSelected = (color: string) => ({
  // iOS fills the selected chip solid with the accent + a beoWhite label (was a 15% outline tint).
  backgroundColor: color,
  border: 'none',
  color: colors.beoWhite,
})

// ============================================================================
// CARD ACCENT PATTERNS
// ============================================================================

/**
 * Card with a full accent border.
 * The accent is carried by a 1px border in the accent color (not a side-stripe —
 * a >1px left/right accent border is the top AI tell; full borders are the on-brand rewrite).
 * For alert/attention cards prefer a leading BeoflowIndicator `.alert` mark instead.
 * @param accentColor - Color for the 1px border
 */
export const createAccentCard = (accentColor: string) => ({
  position: 'relative' as const,
  backgroundColor: `${BEO_WHITE}08`,
  border: `1px solid ${accentColor}`,
  borderRadius: '12px',
})

/**
 * Accordion with a full accent border (see createAccentCard).
 */
export const createAccentAccordion = (accentColor: string) => ({
  backgroundColor: `${BEO_WHITE}08`,
  border: `1px solid ${accentColor}`,
  borderRadius: '8px',
  '&:before': { display: 'none' },
})

// ============================================================================
// CHAT STYLES (iOS-aligned)
// Solid bubble colors matching iOS app — not glass
// ============================================================================

/** User chat bubble — solid meanBlue with iOS xlarge radius */
export const chatBubbleUser = {
  backgroundColor: MEAN_BLUE,
  borderRadius: '16px',
}

/** Assistant chat bubble — solid graphGreyDark with iOS xlarge radius */
export const chatBubbleAssistant = {
  backgroundColor: GRAPH_GREY_DARK,
  borderRadius: '16px',
}

/** Chat input container — liquid glass matching iOS .beoflowGlass(tint: .white.opacity(0.08), cornerRadius: 22) */
export const chatInputContainer = {
  background: `rgba(248, 246, 244, 0.08)`,  // beoWhite at 8% — matches iOS white.opacity(0.08)
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: `1px solid ${BEO_WHITE}26`,        // 15% beoWhite border
  borderRadius: '26px',
}

/** A glass tint that nothing shows through. Glass reveals whatever it floats
 *  over, which is right above a scrolling list and wrong above a structural
 *  edge — the builder's bottom bar reaches across the library rail's border,
 *  and a see-through control lets that border read as a line drawn through it.
 *  Paints the same tint on an opaque base of the page colour instead. */
export const opaqueGlass = (tint: string) => (theme: Theme) =>
  `linear-gradient(${tint}, ${tint}), ${theme.palette.background.default}`

// ============================================================================
// GLASSMORPHISM MATERIALS — iOS .ultraThinMaterial / .thinMaterial / .regularMaterial / .thickMaterial
// ============================================================================
export const glass = {
  ultraThin: {
    background: 'rgba(37, 37, 37, 0.15)',
    backdropBlur: '10px',
    border: `1px solid rgba(248, 246, 244, 0.3)`,
    shadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  },
  thin: {
    background: 'rgba(37, 37, 37, 0.25)',
    backdropBlur: '8px',
    border: `1px solid rgba(248, 246, 244, 0.4)`,
    shadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  regular: {
    background: 'rgba(37, 37, 37, 0.85)',
    backdropBlur: '12px',
    border: `2px solid rgba(248, 246, 244, 0.5)`,
    shadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  },
  thick: {
    background: 'rgba(37, 37, 37, 0.9)',
    backdropBlur: '15px',
    border: `2px solid rgba(248, 246, 244, 0.6)`,
    shadow: '0 12px 40px rgba(0, 0, 0, 0.6)',
  },
  softShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  softShadowMedium: '0 8px 32px rgba(0, 0, 0, 0.4)',
  softShadowLarge: '0 12px 40px rgba(0, 0, 0, 0.5)',
}

type GlassMaterial = typeof glass.ultraThin | typeof glass.thin | typeof glass.regular | typeof glass.thick

/**
 * Creates a full glass overlay style object for MUI component overrides.
 * Bundles background + blur + border + shadow + mobile/a11y media query overrides.
 */
export function createGlassOverlay(material: GlassMaterial, opts?: { borderRadius?: string }) {
  return {
    background: material.background,
    backdropFilter: `blur(${material.backdropBlur})`,
    WebkitBackdropFilter: `blur(${material.backdropBlur})`,
    border: material.border,
    ...(opts?.borderRadius && { borderRadius: opts.borderRadius }),
    boxShadow: material.shadow,
    backgroundImage: 'none',
    '@media (max-width: 768px)': {
      backdropFilter: `blur(${Math.max(5, parseInt(material.backdropBlur) - 2)}px)`,
      WebkitBackdropFilter: `blur(${Math.max(5, parseInt(material.backdropBlur) - 2)}px)`,
    },
    '@media (prefers-reduced-transparency: reduce)': {
      background: 'rgba(37, 37, 37, 0.98)',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
  }
}

/**
 * Shared base styles for glass toggle controls (Checkbox, Radio).
 * Shape-specific overrides (borderRadius) applied per component.
 */
export const glassToggleControl = {
  color: BEO_WHITE,
  '&.Mui-checked': {
    color: BEO_WHITE,
    '& .MuiSvgIcon-root': {
      backgroundColor: '#4A9BB8', // meanBlue — inline to avoid circular dep
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: 'none',
      overflow: 'hidden',
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: '24px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: 'none',
    overflow: 'hidden',
  },
}

// ============================================================================
// FORM GLASS PATTERNS
// ============================================================================

/**
 * Dropdown/Select glass styling
 */
export const glassDropdown = {
  backgroundColor: `${BEO_WHITE}1A`,  // 10% white
  border: `1px solid ${BEO_WHITE}26`,
  borderRadius: '12px',
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Apply glass effect to any MUI sx prop
 * @param variant - 'card' | 'pill' | 'button' | 'dropdown'
 * @param color - Optional color for tinted glass
 */
export function applyGlassEffect(
  variant: 'card' | 'cardSmall' | 'pill' | 'button' | 'dropdown' | 'infoBox',
  color?: string
) {
  const basePatterns = {
    card: glassCard,
    cardSmall: glassCardSmall,
    pill: glassPill,
    button: glassPill,
    dropdown: glassDropdown,
    infoBox: glassInfoBox,
  }

  const basePattern = basePatterns[variant]

  if (color) {
    return {
      ...basePattern,
      backgroundColor: `${color}26`,
      border: `1px solid ${color}`,
    }
  }

  return basePattern
}
